import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react';
import { isAfter, isBefore, isValid } from 'date-fns';
import {
  DateTimeInformation,
  DateTimeSelection,
  DateTimeActions,
  YearSelection,
} from './components';
import { Container } from './styled';
import { defaultTranslateFn } from '../../../utilities';

export const useDateTimePicker = () => {
  const context = useContext(DateTimePickerContext);

  if (!context)
    throw Error('useDateTimePicker can only be used inside DateTimePicker');

  return context;
};

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  minDate,
  maxDate,
  onCancel,
  onSubmit,
  use12HourMode,
  hideDate,
  hideTime,
  t = defaultTranslateFn,
}) => {
  const [selectedValue, setSelectedValue] = useState(
    value && isValid(value)
      ? value
      : getDateInRange(new Date(), minDate, maxDate),
  );
  const [pickerMode, setPickerMode] = useState<PickerMode>('Date');

  useEffect(() => {
    if (value && isValid(value)) {
      setSelectedValue(value);
    }
  }, [value]);

  const contextValue = useMemo(
    () => ({
      hideDate,
      hideTime,
      selectedValue,
      minDate,
      maxDate,
      use12HourMode: !!use12HourMode,
      setSelectedValue,
      onCancel,
      onSubmit,
      pickerMode,
      setPickerMode,
      t,
    }),
    [
      selectedValue,
      setSelectedValue,
      onCancel,
      onSubmit,
      hideDate,
      hideTime,
      use12HourMode,
      minDate,
      maxDate,
      pickerMode,
      setPickerMode,
      t,
    ],
  );

  return (
    <Container>
      <DateTimePickerContext.Provider value={contextValue}>
        <DateTimeInformation t={t} />
        {pickerMode === 'Date' ? <DateTimeSelection /> : <YearSelection />}
        <DateTimeActions />
      </DateTimePickerContext.Provider>
    </Container>
  );
};

type PickerMode = 'Date' | 'Year';

export type DateTimePickerProps = {
  value?: Date;
  minDate?: Date;
  maxDate?: Date;
  use12HourMode?: boolean;
  onCancel?: () => void;
  onSubmit?: (date: Date) => void;
  hideDate?: boolean;
  hideTime?: boolean;
  t?: (key: string) => string;
};

type DateTimePickerContextValue = {
  selectedValue: Date;
  minDate?: Date;
  maxDate?: Date;
  pickerMode: PickerMode;
  setPickerMode: Dispatch<SetStateAction<PickerMode>>;
  setSelectedValue: Dispatch<SetStateAction<Date>>;
  onCancel?: () => void;
  onSubmit?: (date: Date) => void;
  use12HourMode: boolean;
  hideDate?: boolean;
  hideTime?: boolean;
  t: (key: string) => string;
};

const DateTimePickerContext = createContext<
  DateTimePickerContextValue | undefined
>(undefined);

const getDateInRange = (date: Date, minDate?: Date, maxDate?: Date) => {
  if (minDate && isBefore(date, minDate)) return minDate;
  if (maxDate && isAfter(date, maxDate)) return maxDate;
  return date;
};
